/**
 * Resource Type Utilities
 *
 * Provides utilities for detecting and working with resource types
 * based on their key format.
 */

import { ASSET_KEY_PREFIXES, ResourceType } from '@/types';
import { Regex } from '@/utils/regex.util';

/**
 * Determines the resource type from a resource key
 *
 * Analyzes the key format to identify whether it's an Asset, Risk, or Technology.
 * This function is critical for API calls that require explicit type fields.
 *
 * @param key - Resource key (e.g., "#asset#example.com#192.0.2.1")
 * @returns ResourceType string ("asset", "risk", or "technology")
 *
 * @example
 * ```typescript
 * getResourceType("#asset#example.com#192.0.2.1") // "asset"
 * getResourceType("#risk#...") // "risk"
 * getResourceType("#technology#cpe:...") // "technology"
 * ```
 */
export function getResourceType(key: string): ResourceType {
  if (key.match(Regex.RISK)) {
    return ResourceType.Risk;
  }
  if (ASSET_KEY_PREFIXES.find(prefix => key.startsWith(prefix))) {
    return ResourceType.Asset;
  }
  if (key.match(Regex.TECHNOLOGY)) {
    return ResourceType.Technology;
  }

  // Default fallback (should rarely happen with valid keys)
  // Log warning to help debug unexpected key formats
  console.warn('[getResourceType] Could not determine type from key:', key);
  return ResourceType.Asset; // Conservative default
}

/**
 * Validates that a key matches expected format for its resource type
 *
 * Useful for form validation and error handling before making API calls.
 *
 * @param key - Resource key to validate
 * @returns true if key format is valid
 *
 * @example
 * ```typescript
 * isValidResourceKey("#asset#example.com#192.0.2.1") // true
 * isValidResourceKey("invalid-key") // false
 * ```
 */
export function isValidResourceKey(key: string): boolean {
  return Boolean(
    key.match(Regex.RISK) ||
      ASSET_KEY_PREFIXES.some(prefix => key.startsWith(prefix)) ||
      key.match(Regex.TECHNOLOGY)
  );
}
